import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.jsx';
import { ROUTES } from '../lib/constants';
import Button from '../components/common/Button';
import logoImg from '../assets/logo.png';

export default function Login() {
    const navigate = useNavigate();
    const { signIn, isAuthenticated, isParent, isSchool, loading: authLoading } = useAuth();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Redirecionar se já estiver autenticado
    useEffect(() => {
        if (!authLoading && isAuthenticated) {
            console.log('Login: Usuário autenticado, checando perfil...', { isParent, isSchool });
            if (isParent) {
                navigate(ROUTES.APP_PARENT, { replace: true });
            } else if (isSchool) {
                navigate(ROUTES.APP_SCHOOL, { replace: true });
            } else {
                // Se estiver logado mas o profile ainda não carregou, tentamos carregar novamente ou esperamos
                console.warn('Login: Autenticado mas perfil ainda pendente.');
            }
        }
    }, [isAuthenticated, isParent, isSchool, authLoading, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            console.log('Tentando login para:', email);
            await signIn(email, password);
            console.log('Login: Sucesso no Auth');
        } catch (err) {
            console.error('Erro no login:', err);
            setError(err.message || 'Email ou senha incorretos. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    if (authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="text-4xl animate-bounce mb-4">🚗</div>
                    <p className="text-gray-600">Verificando acesso...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
            <div className="max-w-md w-full">
                {/* Logo */}
                <Link to={ROUTES.HOME} className="flex flex-col items-center justify-center gap-3 mb-8">
                    <img src={logoImg} alt="Fila0 Logo" className="h-32 w-auto object-contain" />
                </Link>

                {/* Card de Login */}
                <div className="card">
                    <h2 className="text-2xl font-bold text-textPrimary mb-6 text-center">
                        Entrar na sua conta
                    </h2>

                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-sm">
                            {error === 'Invalid login credentials' ? 'Email ou senha incorretos.' : error}
                        </div>
                    )}

                    {isAuthenticated && !authLoading && profile && !isParent && !isSchool && (
                        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg mb-6 text-sm font-medium">
                            Sua conta ainda não tem um perfil vinculado. Entre em contato com a escola.
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                Email
                            </label>
                            <input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                                placeholder="seu@email.com"
                                required
                            />
                        </div>

                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                                Senha
                            </label>
                            <input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                                placeholder="••••••••"
                                required
                            />
                        </div>

                        <Button
                            type="submit"
                            variant="primary"
                            className="w-full py-4 text-lg"
                            loading={loading}
                        >
                            Entrar
                        </Button>
                    </form>

                    <div className="mt-6 text-center text-sm text-gray-600 font-medium">
                        <p>
                            Ainda não tem conta?{' '}
                            <Link to={ROUTES.REGISTER} className="text-primary font-bold hover:underline underline-offset-4 decoration-2">
                                Crie sua conta grátis
                            </Link>
                        </p>
                    </div>
                </div>

                {/* Link para voltar */}
                <div className="mt-6 text-center">
                    <Link to={ROUTES.HOME} className="text-gray-600 hover:text-primary transition-colors">
                        ← Voltar para home
                    </Link>
                </div>
            </div>
        </div>
    );
}
